/**
 * Notification Inbox - Web Component Style Implementation
 *
 * This is a complete fork of Novu's Web Component logic but implemented in React.
 * Uses low-level @novu/js API with custom caching layer (NovuClientWrapper).
 *
 * Key Differences from Hook-based Implementation:
 * - Uses singleton client wrapper with persistent cache
 * - Cache survives component unmounts
 * - WebSocket listeners are managed by the wrapper
 * - Optimistic UI updates
 * - Matches official Web Component behavior
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import type { RootState } from '@/store'
import {
  MoreHorizontal,
  Settings,
  Archive,
  ArchiveRestore,
  Mail,
  MailOpen,
  Loader2,
  Info,
  ArrowDown,
  BookCheck,
  RefreshCw,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { safeJsonParse } from '@/utils/safeJson'
import { formatDistanceToNow, isThisMonth, isThisWeek, format } from 'date-fns'
import { useNovu } from '@novu/react'
import type { Notification } from '@novu/react'
import { NovuClientWrapper } from './novu-client-wrapper'
import {
  getNotificationClientWrapper,
  setNotificationClientWrapper,
} from './notification-inbox-cache'
import { entityHref, entityTypeDisplay } from './entity-links'
import { nonEmptyString, openNotification } from './notification-enrichment'
import { resolveMainTabSwitch, type InboxMainTab } from './inboxMainTab'
import { AnnouncementList } from '@/features/announcements/AnnouncementList'
import { openAnnouncementDetail } from '@/store/slices/announcementsSlice'
import type { AppDispatch } from '@/store'
import { useTranslation } from 'react-i18next'

type NotificationCategory = 'all' | 'mentions' | 'watching'

interface NotificationFilters {
  category: NotificationCategory
  unreadOnly: boolean
  archived: boolean
}

interface NotificationGroup {
  label: string
  notifications: Notification[]
}

// 单例的持有 + 清理搬到 ./notification-inbox-cache（零运行时依赖），
// 这样 api 层清缓存时不必 import 整个组件。见该文件顶部注释。

interface NotificationInboxWebComponentProps {
  className?: string
  isOpen?: boolean
  /** 请求宿主(铃铛 Popover)关掉自己 —— 打开公告详情时用，详情由全局 host 渲染。 */
  onRequestClose?: () => void
}

export function NotificationInboxWebComponent({
  className,
  isOpen,
  onRequestClose,
}: NotificationInboxWebComponentProps) {
  const { t } = useTranslation('inbox')
  const navigate = useNavigate()
  const novu = useNovu()

  // Get current user ID from Redux store
  const currentUserId = useSelector((state: RootState) => state.user.userId)
  const dispatch = useDispatch<AppDispatch>()

  // 主 tab 三选一（R-044 把顶栏的 Megaphone 入口并了进来）；Novu 那两个 tab 仍由
  // filters.archived 驱动，公告期间冻结不动 —— 判定收在 ./inboxMainTab（纯函数，有单测）。
  const [mainTab, setMainTab] = useState<InboxMainTab>('inbox')
  const isAnnouncementsTab = mainTab === 'announcements'
  // announcement-published 通知不等于公告已读，所以小红点只能读 /pending 集合。
  const hasPendingAnnouncements = useSelector((s: RootState) => s.announcements.items.length > 0)

  // Initialize singleton wrapper
  const clientWrapper = useMemo(() => {
    if (!getNotificationClientWrapper() && novu) {
      const wrapper = new NovuClientWrapper(novu)
      setNotificationClientWrapper(wrapper)

      // Expose to window for debugging (only in development)
      if (import.meta.env.DEV) {
        ;(window as any).__novuClientWrapper = wrapper
      }
    }
    return getNotificationClientWrapper()
  }, [novu])

  // Component state
  const [filters, setFilters] = useState<NotificationFilters>({
    category: 'all',
    unreadOnly: false,
    archived: false,
  })
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [hoveredNotificationId, setHoveredNotificationId] = useState<string | null>(null)
  const [hasPendingNotifications, setHasPendingNotifications] = useState(false) // Track if there are pending notifications
  const [pendingNotificationCount, setPendingNotificationCount] = useState(0) // Count of pending notifications
  const [loadingNotifications, setLoadingNotifications] = useState<Set<string>>(new Set()) // Track notifications being processed
  const [errorNotifications, setErrorNotifications] = useState<Map<string, string>>(new Map()) // Track error messages for notifications
  const [needsSyncBanner, setNeedsSyncBanner] = useState(false) // Track if needs to show sync banner

  // Ref for scroll container
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Banner visibility: show pending banner OR sync banner
  const shouldShowPendingBanner = hasPendingNotifications && !filters.archived && !needsSyncBanner
  const shouldShowSyncBanner = needsSyncBanner

  /**
   * Helper: Set loading state for a notification
   */
  const setNotificationLoading = useCallback((notificationId: string, loading: boolean) => {
    setLoadingNotifications((prev) => {
      const newSet = new Set(prev)
      if (loading) {
        newSet.add(notificationId)
      } else {
        newSet.delete(notificationId)
      }
      return newSet
    })
  }, [])

  /**
   * Helper: Show error message for a notification (auto-dismiss after 3 seconds)
   */
  const showNotificationError = useCallback((notificationId: string, errorMessage: string) => {
    setErrorNotifications((prev) => {
      const newMap = new Map(prev)
      newMap.set(notificationId, errorMessage)
      return newMap
    })

    // Auto-dismiss after 3 seconds
    setTimeout(() => {
      setErrorNotifications((prev) => {
        const newMap = new Map(prev)
        newMap.delete(notificationId)
        return newMap
      })
    }, 3000)
  }, [])

  /**
   * Format timestamp helper with specific time
   * Example: "less than a minute ago (2025/10/20 19:24:05)"
   */
  const formatTimestamp = (timestamp: string | Date) => {
    try {
      const date = new Date(timestamp)
      const relativeTime = formatDistanceToNow(date, { addSuffix: true })
      const specificTime = format(date, 'yyyy/MM/dd HH:mm:ss') // Full date time format
      return `${relativeTime} (${specificTime})`
    } catch {
      return t('notifications.unknownTime')
    }
  }

  // Convert a special task's subtype code (or its title) to a user-readable label
  const SPECIAL_TASK_LABELS: Record<string, string> = {
    'special.meeting': 'Meeting',
    'special.office_work': 'Office Work',
    'special.team_support': 'Team Support',
    'special.shopping': 'Shopping',
    'special.training': 'Training',
    'special.driving': 'Driving',
  }

  const getReadableTaskTitle = (task: { title?: string; taskTypeCode?: string; taskSubtypeCode?: string }) => {
    if (task.taskTypeCode === 'special' || task.title?.startsWith('special.')) {
      const code = task.taskSubtypeCode || task.title || ''
      return SPECIAL_TASK_LABELS[code] || code.replace('special.', '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    }
    return task.title || t('notifications.untitledTask')
  }

  /**
   * Parse notification body safely
   * Handles both string and object types, and deals with malformed JSON
   * Also handles special quote characters (Chinese quotes, curly quotes, etc.)
   */
  const parseNotificationBody = (body: any): any => {
    if (!body) return {}

    // If already an object, return as-is
    if (typeof body === 'object') {
      return body
    }

    // If string, try to parse
    if (typeof body === 'string') {
      // 早返回:body 不像 JSON 就别瞎试。
      // Novu workflow body 模板可能直接渲染成 plain text (例如评论原文 @[id|name] ...),
      // 此时 JSON.parse 必挂、容错策略也救不回来,只会刷屏 console。
      // 跟 mobile parseNotificationBody 行为对齐:把 plain text 包成 {message: text}。
      const trimmed = body.trim()
      if (trimmed && !trimmed.startsWith('{') && !trimmed.startsWith('[')) {
        return { message: trimmed }
      }

      try {
        // First, try direct parse (works for properly formatted JSON)
        return safeJsonParse(body)
      } catch {
        try {
          // Pre-process: normalize special quote characters to standard quotes
          let cleaned = body

          // Replace Chinese quotes with standard English quotes
          cleaned = cleaned.replace(/"/g, '"')  // Left Chinese double quote
          cleaned = cleaned.replace(/"/g, '"')  // Right Chinese double quote
          cleaned = cleaned.replace(/'/g, "'")  // Left Chinese single quote
          cleaned = cleaned.replace(/'/g, "'")  // Right Chinese single quote

          // Replace curly/smart quotes with straight quotes
          cleaned = cleaned.replace(/[""]/g, '"')  // Left and right double curly quotes
          cleaned = cleaned.replace(/['']/g, "'")  // Left and right single curly quotes

          // Try parsing after normalization
          try {
            return safeJsonParse(cleaned)
          } catch {
            // Continue to smart quote replacement
          }

          // Smart quote replacement: only replace single quotes that are used as string delimiters
          // This preserves apostrophes in contractions like "task's" or "don't"

          // Step 1: Temporarily replace escaped single quotes to protect them
          cleaned = cleaned.replace(/\\'/g, '\x00ESCAPED_SINGLE_QUOTE\x00')

          // Step 2: Replace single quotes that are string delimiters
          // Pattern: single quote after : or [ or { or ,
          cleaned = cleaned.replace(/([:,\[\{])\s*'/g, '$1"')
          // Pattern: single quote before : or ] or } or ,
          cleaned = cleaned.replace(/'\s*([:,\]\}])/g, '"$1')

          // Step 3: Restore escaped single quotes
          cleaned = cleaned.replace(/\x00ESCAPED_SINGLE_QUOTE\x00/g, "\\'")

          return safeJsonParse(cleaned)
        } catch {
          try {
            // If smart replacement fails, try a more aggressive approach
            // Replace ALL single quotes with double quotes, then fix known apostrophes
            let cleaned = body

            // First normalize special quotes again
            cleaned = cleaned.replace(/[""]/g, '"')
            cleaned = cleaned.replace(/[""]/g, '"')
            cleaned = cleaned.replace(/['']/g, "'")
            cleaned = cleaned.replace(/['']/g, "'")

            // Replace all single quotes
            cleaned = cleaned.replace(/'/g, '"')

            // Fix common apostrophe patterns
            cleaned = cleaned.replace(/(\w)"s\s/g, "$1's ")  // fix "task"s" -> "task's"
            cleaned = cleaned.replace(/(\w)"t\s/g, "$1't ")  // fix "don"t" -> "don't"
            cleaned = cleaned.replace(/(\w)"re\s/g, "$1're ") // fix "you"re" -> "you're"
            cleaned = cleaned.replace(/(\w)"ll\s/g, "$1'll ") // fix "we"ll" -> "we'll"
            cleaned = cleaned.replace(/(\w)"ve\s/g, "$1've ") // fix "I"ve" -> "I've"
            cleaned = cleaned.replace(/(\w)"d\s/g, "$1'd ")   // fix "he"d" -> "he'd"
            cleaned = cleaned.replace(/(\w)"m\s/g, "$1'm ")   // fix "I"m" -> "I'm"

            // Fix apostrophes at end of words (before punctuation)
            cleaned = cleaned.replace(/(\w)"s"/g, "$1's\"")  // fix "task"s"" -> "task's""
            cleaned = cleaned.replace(/(\w)"s'/g, "$1's'")   // fix "task"s'" -> "task's'"
            cleaned = cleaned.replace(/(\w)"s\./g, "$1's.")  // fix "task"s." -> "task's."
            cleaned = cleaned.replace(/(\w)"s,/g, "$1's,")   // fix "task"s," -> "task's,"
            cleaned = cleaned.replace(/(\w)"s}/g, "$1's}")   // fix "task"s}" -> "task's}"

            return safeJsonParse(cleaned)
          } catch (e3) {
            console.error('[Parse Body] All parsing attempts failed. Body:', body)
            console.error('[Parse Body] Error details:', e3)
            return {}
          }
        }
      }
    }

    return {}
  }

  /**
   * 把评论 mention 文本切成 segments,跟 mobile parseMentionSegments / CommentCard.renderMentionAwareText 一致。
   * 输入 `@[7333|Nova Admin] ok` → [{type:'mention', text:'@Nova Admin', userId:'7333'}, {type:'text', text:' ok'}]
   */
  type MentionSegment =
    | { type: 'mention'; text: string; userId: string }
    | { type: 'text'; text: string }
  const parseMentionSegments = (content: string): MentionSegment[] => {
    if (!content) return []
    const segments: MentionSegment[] = []
    const regex = /@\[([^|\]]+)\|([^\]]+)\]/g
    let lastIndex = 0
    let match: RegExpExecArray | null
    while ((match = regex.exec(content)) !== null) {
      if (match.index > lastIndex) {
        segments.push({ type: 'text', text: content.slice(lastIndex, match.index) })
      }
      segments.push({ type: 'mention', text: `@${match[2]}`, userId: match[1] })
      lastIndex = regex.lastIndex
    }
    if (lastIndex < content.length) {
      segments.push({ type: 'text', text: content.slice(lastIndex) })
    }
    return segments
  }

  /**
   * 渲染 mention 评论文本:`@xxx` 紫色加粗,普通文本原样。
   * 跟 mobile NotificationItem 的 mention 渲染 (text-purple-600 font-semibold) 完全一致。
   */
  const renderMentionContent = (content: string): React.ReactNode => {
    const segments = parseMentionSegments(content)
    return segments.map((seg, i) =>
      seg.type === 'mention' ? (
        <span key={i} className="text-purple-600 font-semibold">{seg.text}</span>
      ) : (
        <span key={i}>{seg.text}</span>
      )
    )
  }

  /**
   * 把 msg-mentioned / msg-send body payload 规一化成 { sender, entity, message } 形状。
   * 支持两种 payload:
   *   - 当前(msg-mentioned): { comment: {entityTypeCode, entityId, content, entityTitle?}, mentioner: {fullName, avatarUrl} }
   *   - 旧: { sender: {...}, entity: {...}, message: '...' }
   *
   * 兼容策略(跟 mobile NotificationItem.tsx 对齐):
   * 1. 检测新 payload 只要 comment.entityTypeCode + comment.entityId,mentioner 不强制
   *    (Novu workflow Data object 可能只暴露 comment;mentioner/sender 字段名也可能两种都有)
   * 2. sender 数据从 parsed.mentioner → parsed.sender 依次取,avatar 字段试 avatarUrl / avatar_url
   * 3. 没匹配的字段留空,render 层 fallback (例如从 subject 解析名字、用 notification.avatar)
   */
  // Novu workflow Data Object 有时会把 comment / mentioner / sender / entity 这种
  // 嵌套对象存成 stringified JSON,这里 best-effort 解一下,失败/非 JSON 字符串保持原样。
  const tryParseJSONField = (val: any): any => {
    if (typeof val !== 'string') return val
    const trimmed = val.trim()
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return val
    try {
      return JSON.parse(trimmed)
    } catch {
      return val
    }
  }

  const normalizeMessagePayload = (parsed: any): { sender?: any; entity?: any; message?: string } => {
    if (!parsed || typeof parsed !== 'object') return {}
    const comment = tryParseJSONField(parsed.comment)
    const mentioner = tryParseJSONField(parsed.mentioner)
    const senderTop = tryParseJSONField(parsed.sender)
    const entityTop = tryParseJSONField(parsed.entity)
    const isObj = (v: any) => v && typeof v === 'object' && !Array.isArray(v)
    // 新 payload:comment 带 entityTypeCode + entityId 就进 mention 分支,不强制 mentioner —
    // mentioner / sender 字段不一定填齐,逐个 fallback 兜底。
    if (isObj(comment) && comment.entityTypeCode && comment.entityId) {
      const etCode = String(comment.entityTypeCode)
      const senderRaw =
        (isObj(mentioner) && mentioner.fullName ? mentioner : undefined) ||
        (isObj(senderTop) && senderTop.fullName ? senderTop : undefined)
      const sender = senderRaw
        ? {
            fullName: String(senderRaw.fullName),
            avatarUrl: senderRaw.avatarUrl || senderRaw.avatar_url || undefined,
          }
        : undefined
      return {
        sender,
        entity: {
          type: etCode,
          id: String(comment.entityId),
          // 后端 comment.entityTitle 是真实 task/intent 标题;老通知没塞就退到 parsed.entity?.title;
          // 都没有 → 留空字符串,渲染层会用 type 首字母大写兜底(例如 "Task")
          title:
            comment.entityTitle ||
            (isObj(entityTop) ? entityTop.title : '') ||
            '',
        },
        // 保留 raw content(`@[id|Name] xxx`形态)给渲染层做高亮分段
        message: comment.content || '',
      }
    }
    // 旧 payload:原样透出 (sender/entity 仍尝试解 stringified JSON)
    return {
      sender: isObj(senderTop) ? senderTop : undefined,
      entity: isObj(entityTop) ? entityTop : undefined,
      message: parsed.message,
    }
  }

  /**
   * Extract sender name from notification subject as last-resort fallback.
   * 支持两种 subject 格式:
   *   - 前置:"Jason Wang send you a message" / "Jason Wang mentioned you"
   *   - 后置:"You are mentioned by Jason Wang" / "You received a message from Jason Wang"
   * 后置格式末尾可能带 "." (Novu template `{{payload.sender.fullName}}.` 渲染),需要剥掉。
   */
  const extractSenderFromSubject = (subject: string): string => {
    if (!subject) return ''
    const trimmed = subject.trim()
    // 前置:把后缀短语 strip 掉,剩下的就是名字
    const prefixPatterns = [
      /\s+send you a message.*/i,
      /\s+mentioned you.*/i,
      /\s+sent you.*/i,
    ]
    for (const pattern of prefixPatterns) {
      const name = trimmed.replace(pattern, '').trim()
      if (name && name !== trimmed) return name
    }
    // 后置:正则匹配前缀短语后捕获名字
    const suffixPatterns = [
      /^You are mentioned by\s+(.+?)\.?$/i,
      /^You received a message from\s+(?:user:\s*)?(.+?)\.?$/i,
    ]
    for (const pattern of suffixPatterns) {
      const match = trimmed.match(pattern)
      if (match && match[1] && match[1].trim() && match[1].trim() !== '.') {
        return match[1].trim()
      }
    }
    return ''
  }

  /**
   * Determine which categories a notification belongs to based on body data
   * Returns an object with boolean flags for each category
   */
  const getNotificationCategories = useCallback((notification: Notification): {
    all: boolean
    mentions: boolean
    watching: boolean
  } => {
    // All notifications belong to 'all' category
    const categories = { all: true, mentions: false, watching: false }

    // If no current user ID, can't determine mentions/watching
    if (!currentUserId) {
      return categories
    }

    // Pre-rule: msg-mentioned workflow 直接归到 Mentions。后端在 Novu trigger
    // 时没设 subscribers.linkTypes='mentioned'，所以下面的 subscribers 判断会
    // 把它误归到 Watching。按 workflow identifier 优先匹配是更稳的做法。
    const workflowId = (notification as any).templateIdentifier
      ?? (notification as any).workflow?.identifier
    if (workflowId === 'msg-mentioned') {
      categories.mentions = true
      return categories
    }

    try {
      // Parse body using safe parser, with notification.data as primary source
      const payloadData = notification.data as any
      const bodyData = parseNotificationBody(notification.body)

      // Extract payload data - prefer notification.data (raw payload), fallback to body parsing
      const payload = bodyData.payload || bodyData
      const data = payloadData || payload.data || payload
      // Support both 'subscribers' and 'subscriber' field names
      const subscribers = data.subscribers || data.subscriber || bodyData.subscribers || bodyData.subscriber

      // Rule 1: No subscribers → all + watching
      if (!subscribers || typeof subscribers !== 'object') {
        categories.watching = true
        return categories
      }

      // Rule 2: Has subscribers but current user not in it → all + watching
      const currentUserSubscriber = subscribers[currentUserId]
      if (!currentUserSubscriber) {
        categories.watching = true
        return categories
      }

      // Rule 3: Current user is in subscribers
      const linkTypes = currentUserSubscriber.linkTypes || []
      const hasMentioned = linkTypes.includes('mentioned')
      const hasOtherTypes = linkTypes.some((type: string) => type !== 'mentioned')

      console.log('[Notification Category] Processing:', {
        notificationId: notification.id,
        currentUserId,
        linkTypes,
        hasMentioned,
        hasOtherTypes
      })

      if (hasMentioned && !hasOtherTypes) {
        // Only mentioned → all + mentions
        categories.mentions = true
      } else if (hasMentioned && hasOtherTypes) {
        // Mentioned + other types → all + mentions + watching
        categories.mentions = true
        categories.watching = true
      } else {
        // No mentioned, only other types → all + watching
        categories.watching = true
      }

    } catch (error) {
      console.error('[Notification Category] Error parsing body:', error, notification.body)
      // On error, default to watching
      categories.watching = true
    }

    console.log('[Notification Category] Result:', {
      notificationId: notification.id,
      categories
    })

    return categories
  }, [currentUserId])

  /**
   * 当前 tab 对应的服务端 list 过滤条件。
   * Unread Only 必须走服务端过滤(read:false,独立缓存键):未读通知可能埋在分页
   * 深处(第 N 页),铃铛徽标数来自服务端全量 count,若只在客户端过滤已加载的
   * 前几页,会出现"徽标有数、Unread Only 列表为空"且无内容可滚动触发加载更多。
   */
  const serverFilter = useMemo(
    () => ({
      archived: filters.archived,
      snoozed: false,
      ...(filters.unreadOnly && !filters.archived ? { read: false } : {}),
    }),
    [filters.archived, filters.unreadOnly]
  )

  /**
   * Group notifications by time period
   */
  const groupedNotifications: NotificationGroup[] = useMemo(() => {
    const groups: NotificationGroup[] = []
    const thisMonth: Notification[] = []
    const lastWeek: Notification[] = []
    const older: Notification[] = []

    // Apply client-side filters
    let filtered = notifications

    // Category filter - use body parsing instead of tags
    if (filters.category === 'mentions') {
      filtered = filtered.filter((n) => {
        const categories = getNotificationCategories(n)
        return categories.mentions
      })
    } else if (filters.category === 'watching') {
      filtered = filtered.filter((n) => {
        const categories = getNotificationCategories(n)
        return categories.watching
      })
    }
    // 'all' category shows everything, no filtering needed

    // Unread filter - only apply in Inbox tab
    if (filters.unreadOnly && !filters.archived) {
      filtered = filtered.filter((n) => !n.isRead)
    }

    // Group by time
    filtered.forEach((notification) => {
      const date = new Date(notification.createdAt)
      if (isThisMonth(date)) {
        thisMonth.push(notification)
      } else if (isThisWeek(date)) {
        lastWeek.push(notification)
      } else {
        older.push(notification)
      }
    })

    if (thisMonth.length > 0) groups.push({ label: t('notifications.groups.thisMonth'), notifications: thisMonth })
    if (lastWeek.length > 0) groups.push({ label: t('notifications.groups.lastWeek'), notifications: lastWeek })
    if (older.length > 0) groups.push({ label: t('notifications.groups.older'), notifications: older })

    return groups
  }, [notifications, filters.category, filters.unreadOnly, filters.archived, getNotificationCategories, t])

  /**
   * Load notifications
   */
  const loadNotifications = useCallback(
    async (forceRefresh = false) => {
      if (!clientWrapper) {
        return
      }


      if (forceRefresh) {
        clientWrapper.clearCache(serverFilter)
      }

      // 有缓存（哪怕已过 5 分钟有效期）就先显示缓存、不出整屏 loading，list() 拿到新数据再替换
      const cached = forceRefresh ? null : clientWrapper.getCacheData(serverFilter)
      if (cached) {
        setNotifications(cached.notifications.map((n) => clientWrapper.applyKnownEnrichment(n)))
        setHasMore(cached.hasMore)
      } else {
        setIsLoading(true)
      }

      const response = await clientWrapper.list({
        ...serverFilter,
        limit: 10,
      })

      if (response.data) {
        setNotifications(response.data.notifications)
        setHasMore(response.data.hasMore)
        console.log('[NotificationInbox] Load notifications complete:', {
          count: response.data.notifications.length,
          hasMore: response.data.hasMore,
          archived: serverFilter.archived
        })
      }

      setIsLoading(false)
    },
    [clientWrapper, serverFilter]
  )

  /**
   * Load more (pagination)
   */
  const handleLoadMore = useCallback(async () => {
    if (!clientWrapper || !hasMore || isFetching) return

    setIsFetching(true)

    try {
      const lastNotification = notifications[notifications.length - 1]


      const response = await clientWrapper.list({
        ...serverFilter,
        limit: 10,
        after: lastNotification?.id,
      })

      if (response.data && response.data.notifications.length > 0) {
        // Append new notifications to existing list
        setNotifications((prev) => {
          const notificationMap = new Map<string, Notification>()

          // Add existing notifications first (these have correct state)
          prev.forEach((n) => {
            notificationMap.set(n.id, n)
          })

          // Add new notifications ONLY if they don't exist yet
          // This prevents SDK data from overwriting local state
          response.data!.notifications.forEach((n) => {
            if (!notificationMap.has(n.id)) {
              // New notification, add it
              notificationMap.set(n.id, n)
            }
            // If notification already exists in local cache, skip it
            // This preserves the correct state (read/unread) from local cache
          })

          const mergedList = Array.from(notificationMap.values())

          console.log('[Load More] Merged notifications:', {
            previous: prev.length,
            fromAPI: response.data!.notifications.length,
            merged: mergedList.length,
            newAdded: mergedList.length - prev.length
          })

          return mergedList
        })

        setHasMore(response.data.hasMore)
      } else {
        setHasMore(false)
      }
    } catch {
    } finally {
      setIsFetching(false)
    }
  }, [clientWrapper, hasMore, isFetching, notifications, serverFilter])

  /**
   * Handle notification click
   */
  const handleNotificationClick = useCallback(
    async (notification: Notification) => {
      if (!clientWrapper) return

      const markRead = async () => {
        if (notification.isRead) return
        setNotificationLoading(notification.id, true)
        try {
          await clientWrapper.read(notification.id)

          // Optimistic update
          setNotifications((prev) =>
            prev.map((n) => (n.id === notification.id ? ({ ...n, isRead: true } as Notification) : n))
          )
        } catch (error) {
          console.error('Failed to mark as read:', error)
          showNotificationError(notification.id, 'Failed to mark as read')
        } finally {
          setNotificationLoading(notification.id, false)
        }
      }

      // data 里已有 url 立刻跳；没有才等 payload 补齐（与标已读并行）后再取。空串 url 不算有地址
      await openNotification(notification, {
        ensureEnriched: (n) => clientWrapper.ensureEnriched(n),
        markRead,
        navigate: (url) => void navigate(url),
      })
    },
    [clientWrapper, navigate, setNotificationLoading, showNotificationError]
  )

  /**
   * Toggle read status
   */
  const handleToggleRead = useCallback(
    async (notification: Notification, event: React.MouseEvent) => {
      event.stopPropagation()
      if (!clientWrapper) return

      setNotificationLoading(notification.id, true)

      try {
        if (notification.isRead) {
          await clientWrapper.unread(notification.id)
        } else {
          await clientWrapper.read(notification.id)
        }

        // Optimistic update
        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? ({ ...n, isRead: !n.isRead } as Notification) : n))
        )
      } catch (error) {
        console.error('Failed to toggle read status:', error)
        showNotificationError(notification.id, 'Failed to update read status')
      } finally {
        setNotificationLoading(notification.id, false)
      }
    },
    [clientWrapper, setNotificationLoading, showNotificationError]
  )

  /**
   * Archive notification
   */
  const handleArchive = useCallback(
    async (notification: Notification, event: React.MouseEvent) => {
      event.stopPropagation()
      if (!clientWrapper) return

      setNotificationLoading(notification.id, true)

      try {
        await clientWrapper.archive(notification.id)

        // Optimistically update the notification state
        // Note: archive API also marks the notification as read
        // spread 丢失 Notification 类原型方法(仅作数据对象消费),cast 保持行为
        const archivedNotification = {
          ...notification,
          isArchived: true,
          isRead: true, // Archive API automatically marks as read
        } as Notification

        // Remove from current displayed list and check if it becomes empty
        let shouldRefresh = false
        setNotifications((prev) => {
          const filtered = prev.filter((n) => n.id !== notification.id)
          // If UI becomes empty after removing, we should refresh
          if (filtered.length === 0) {
            shouldRefresh = true
          }
          return filtered
        })

        // If UI is now empty, fetch fresh data directly
        if (shouldRefresh) {
          const response = await clientWrapper.list({
            ...serverFilter,
            limit: 10,
          })

          if (response.data && response.data.notifications.length > 0) {
            // Has data: update UI and cache
            setNotifications(response.data.notifications)
            setHasMore(response.data.hasMore)
            clientWrapper.updateCacheManually(
              serverFilter,
              response.data.notifications,
              response.data.hasMore
            )
          } else {
            // No data: clear UI and cache
            setNotifications([])
            setHasMore(false)
            clientWrapper.clearCache(serverFilter)
          }
          return
        }

        // Update both Inbox and Archive caches directly (no need to refetch from API)
        // Bug fix: Ensure notification is properly moved between caches
        if (!filters.archived) {
          // We're on Inbox tab, archiving a notification
          console.log('[NotificationInbox] Archiving notification from Inbox, updating both caches')

          // 1. Update Inbox cache - remove the archived notification
          const inboxCache = clientWrapper.getCacheData({ archived: false, snoozed: false, tags: [] })
          if (inboxCache) {
            const updatedInboxList = inboxCache.notifications.filter((n) => n.id !== notification.id)
            clientWrapper.updateCacheManually(
              { archived: false, snoozed: false, tags: [] },
              updatedInboxList,
              inboxCache.hasMore
            )
            console.log('[NotificationInbox] Removed notification from Inbox cache:', notification.id)
          }

          // 2. Update Archive cache - add the archived notification
          const archiveCache = clientWrapper.getCacheData({ archived: true, snoozed: false, tags: [] })
          if (archiveCache) {
            // Add the archived notification to the top of Archive cache
            const updatedArchiveList = [archivedNotification, ...archiveCache.notifications]
            clientWrapper.updateCacheManually(
              { archived: true, snoozed: false, tags: [] },
              updatedArchiveList,
              archiveCache.hasMore
            )
            console.log('[NotificationInbox] Added notification to Archive cache:', notification.id)
          } else {
            // Archive cache doesn't exist yet, create it with this notification
            clientWrapper.updateCacheManually(
              { archived: true, snoozed: false, tags: [] },
              [archivedNotification],
              false
            )
            console.log('[NotificationInbox] Created Archive cache with notification:', notification.id)
          }

          // 3. Unread Only 视图有独立缓存键(read:false),同步移除已归档的这条
          if (filters.unreadOnly) {
            const unreadFilter = { archived: false, snoozed: false, read: false, tags: [] }
            const unreadCache = clientWrapper.getCacheData(unreadFilter)
            if (unreadCache) {
              clientWrapper.updateCacheManually(
                unreadFilter,
                unreadCache.notifications.filter((n) => n.id !== notification.id),
                unreadCache.hasMore
              )
            }
          }
        } else {
          // If we're on Archive tab (unarchiving scenario), refresh
          await loadNotifications(true)
        }
      } catch (error) {
        console.error('Failed to archive:', error)
        showNotificationError(notification.id, 'Failed to archive')
        // Restore the notification on error
        await loadNotifications(true)
      } finally {
        setNotificationLoading(notification.id, false)
      }
    },
    [clientWrapper, loadNotifications, setNotificationLoading, showNotificationError, filters.archived, filters.unreadOnly, serverFilter]
  )

  /**
   * Unarchive notification
   */
  const handleUnarchive = useCallback(
    async (notification: Notification, event: React.MouseEvent) => {
      event.stopPropagation()
      if (!clientWrapper) return

      setNotificationLoading(notification.id, true)

      try {
        await clientWrapper.unarchive(notification.id)

        // Optimistically update the notification state
        // Note: unarchive API also marks the notification as read
        const unarchivedNotification = {
          ...notification,
          isArchived: false,
          isRead: true, // Unarchive API automatically marks as read
        } as Notification

        // Remove from current displayed list and check if it becomes empty
        let shouldRefresh = false
        setNotifications((prev) => {
          const filtered = prev.filter((n) => n.id !== notification.id)
          // If UI becomes empty after removing, we should refresh
          if (filtered.length === 0) {
            shouldRefresh = true
          }
          return filtered
        })

        // If UI is now empty, fetch fresh data directly
        if (shouldRefresh) {
          const response = await clientWrapper.list({
            ...serverFilter,
            limit: 10,
          })

          if (response.data && response.data.notifications.length > 0) {
            // Has data: update UI and cache
            setNotifications(response.data.notifications)
            setHasMore(response.data.hasMore)
            clientWrapper.updateCacheManually(
              serverFilter,
              response.data.notifications,
              response.data.hasMore
            )
          } else {
            // No data: clear UI and cache
            setNotifications([])
            setHasMore(false)
            clientWrapper.clearCache(serverFilter)
          }
          return
        }

        // Update both Archive and Inbox caches directly (no need to refetch from API)
        // Bug fix: Ensure notification is properly moved between caches
        if (filters.archived) {
          // We're on Archive tab, unarchiving a notification
          console.log('[NotificationInbox] Unarchiving notification from Archive, updating both caches')

          // 1. Update Archive cache - remove the unarchived notification
          const archiveCache = clientWrapper.getCacheData({ archived: true, snoozed: false, tags: [] })
          if (archiveCache) {
            const updatedArchiveList = archiveCache.notifications.filter((n) => n.id !== notification.id)
            clientWrapper.updateCacheManually(
              { archived: true, snoozed: false, tags: [] },
              updatedArchiveList,
              archiveCache.hasMore
            )
            console.log('[NotificationInbox] Removed notification from Archive cache:', notification.id)
          }

          // 2. Update Inbox cache - add the unarchived notification to the top
          const inboxCache = clientWrapper.getCacheData({ archived: false, snoozed: false, tags: [] })
          if (inboxCache) {
            // Add the unarchived notification to the top of Inbox cache
            const updatedInboxList = [unarchivedNotification, ...inboxCache.notifications]
            clientWrapper.updateCacheManually(
              { archived: false, snoozed: false, tags: [] },
              updatedInboxList,
              inboxCache.hasMore
            )
            console.log('[NotificationInbox] Added notification to Inbox cache:', notification.id)
          } else {
            // Inbox cache doesn't exist yet, create it with this notification
            clientWrapper.updateCacheManually(
              { archived: false, snoozed: false, tags: [] },
              [unarchivedNotification],
              false
            )
            console.log('[NotificationInbox] Created Inbox cache with notification:', notification.id)
          }
        } else {
          // If we're on Inbox tab (shouldn't happen, but handle it), refresh
          await loadNotifications(true)
        }
      } catch (error) {
        console.error('Failed to unarchive:', error)
        showNotificationError(notification.id, 'Failed to unarchive')
        // Restore the notification on error
        await loadNotifications(true)
      } finally {
        setNotificationLoading(notification.id, false)
      }
    },
    [clientWrapper, loadNotifications, setNotificationLoading, showNotificationError, filters.archived, serverFilter]
  )

  /**
   * Mark all as read
   * Only updates cache, no additional data fetching
   */
  const handleMarkAllAsRead = useCallback(async () => {
    if (!clientWrapper) return

    try {
      // If there are pending notifications, load them first
      if (hasPendingNotifications) {
        const pendingNotifications = clientWrapper.getPendingNotifications()

        // Merge pending with current list and update cache
        let mergedList: Notification[] = []
        setNotifications((prev) => {
          const notificationMap = new Map<string, Notification>()
          pendingNotifications.forEach((n) => notificationMap.set(n.id, n))
          prev.forEach((n) => {
            if (!notificationMap.has(n.id)) notificationMap.set(n.id, n)
          })
          mergedList = Array.from(notificationMap.values())
          return mergedList
        })

        // Update cache with merged list
        clientWrapper.updateCacheManually(serverFilter, mergedList, hasMore)

        // Clear pending state
        clientWrapper.clearPendingNotifications()
        setHasPendingNotifications(false)
        setPendingNotificationCount(0)
      }

      // Call API - cache is already updated optimistically in clientWrapper
      await clientWrapper.readAll({
        tags: []
      })

      // Update local state - mark all as read
      setNotifications(prevNotifications =>
        prevNotifications.map(notification => ({
          ...notification,
          isRead: true
        }) as Notification)
      )

    } catch (error) {
      console.error('Failed to mark all as read:', error)
      // On error, reload from server
      await loadNotifications(true)
    }
  }, [clientWrapper, loadNotifications, hasPendingNotifications, serverFilter, hasMore])

  /**
   * Archive all
   */
  const handleArchiveAll = useCallback(async () => {
    if (!clientWrapper) return

    try {
      // If there are pending notifications, load them first
      if (hasPendingNotifications) {
        const pendingNotifications = clientWrapper.getPendingNotifications()

        // Merge pending with current list and update cache
        let mergedList: Notification[] = []
        setNotifications((prev) => {
          const notificationMap = new Map<string, Notification>()
          pendingNotifications.forEach((n) => notificationMap.set(n.id, n))
          prev.forEach((n) => {
            if (!notificationMap.has(n.id)) notificationMap.set(n.id, n)
          })
          mergedList = Array.from(notificationMap.values())
          return mergedList
        })

        // Update cache with merged list
        clientWrapper.updateCacheManually(serverFilter, mergedList, hasMore)

        // Clear pending state
        clientWrapper.clearPendingNotifications()
        setHasPendingNotifications(false)
        setPendingNotificationCount(0)
      }

      // Call API - wrapper will sync cache automatically using Feed API
      // IMPORTANT: Pass the same filter used for listing to ensure correct cache key
      await clientWrapper.archiveAll({
        tags: [],
        snoozed: false  // Must match the filter used in loadNotifications
      })

      // Reload from cache (wrapper has already synced with Feed API)
      // Unread Only 视图的缓存键(read:false)不在 wrapper 同步范围内,强制刷新
      await loadNotifications(filters.unreadOnly)
    } catch (error) {
      console.error('Failed to archive all:', error)
      // On error, reload from server
      await loadNotifications(true)
    }
  }, [clientWrapper, loadNotifications, filters.unreadOnly, serverFilter, hasPendingNotifications, hasMore])

  /**
   * Archive all read notifications
   * Uses the read-archive endpoint
   */
  const handleArchiveRead = useCallback(async () => {
    if (!clientWrapper) return

    try {

      // If there are pending notifications, load them first
      if (hasPendingNotifications) {
        const pendingNotifications = clientWrapper.getPendingNotifications()

        // Merge pending with current list and update cache
        let mergedList: Notification[] = []
        setNotifications((prev) => {
          const notificationMap = new Map<string, Notification>()
          pendingNotifications.forEach((n) => notificationMap.set(n.id, n))
          prev.forEach((n) => {
            if (!notificationMap.has(n.id)) notificationMap.set(n.id, n)
          })
          mergedList = Array.from(notificationMap.values())
          return mergedList
        })

        // Update cache with merged list
        clientWrapper.updateCacheManually(serverFilter, mergedList, hasMore)

        // Clear pending state
        clientWrapper.clearPendingNotifications()
        setHasPendingNotifications(false)
        setPendingNotificationCount(0)
      }

      // Call API - wrapper will sync cache automatically using Feed API
      // IMPORTANT: Pass the same filter used for listing to ensure correct cache key
      await clientWrapper.archiveRead({
        tags: [],
        snoozed: false  // Must match the filter used in loadNotifications
      })

      // Reload from cache (wrapper has already synced with Feed API)
      // Unread Only 视图的缓存键(read:false)不在 wrapper 同步范围内,强制刷新
      await loadNotifications(filters.unreadOnly)
    } catch (error) {
      console.error('Failed to archive read notifications:', error)
      // On error, reload from server
      await loadNotifications(true)
    }
  }, [clientWrapper, loadNotifications, filters.unreadOnly, serverFilter, hasPendingNotifications, hasMore])

  /**
   * Navigate to settings
   */
  const handleSettingsClick = useCallback(() => {
    void navigate('/profile#notifications')
  }, [navigate])

  /**
   * Handle "Load new notifications" button click
   */
  /**
   * Handle sync banner refresh button click
   */
  const handleSyncRefresh = useCallback(async () => {
    if (!clientWrapper) return

    console.log('[NotificationInbox] Handling sync refresh for tab:', filters.archived ? 'Archive' : 'Inbox')

    // Bug fix: Clear pending notifications before syncing
    // This prevents outdated pending notifications from being loaded after sync
    clientWrapper.clearPendingNotifications()
    setHasPendingNotifications(false)
    console.log('[NotificationInbox] Cleared pending notifications before sync')

    // Hide sync banner
    setNeedsSyncBanner(false)

    // Clear sync flags
    clientWrapper.clearSyncFlags()

    // Use syncCacheFromFeedAPI to update both Inbox and Archive caches
    // This ensures both caches are in sync with the server
    setIsLoading(true)

    try {
      // Sync both Inbox and Archive caches from Feed API
      await clientWrapper.syncCacheFromFeedAPI()

      // Unread Only 视图的缓存键(read:false)不在 syncCacheFromFeedAPI 同步范围内,
      // 先清掉让下面的 list 直接打 API 拿最新数据
      if (filters.unreadOnly && !filters.archived) {
        clientWrapper.clearCache(serverFilter)
      }

      const response = await clientWrapper.list({
        ...serverFilter,
        tags: [],
        limit: 20 // Load initial set, actual limit is determined by syncCacheFromFeedAPI
      })

      if (response.data) {
        setNotifications(response.data.notifications)
        setHasMore(response.data.hasMore)
        console.log('[NotificationInbox] Sync refresh complete, loaded', response.data.notifications.length, 'notifications')
      }
    } catch (error) {
      console.error('[NotificationInbox] Sync refresh failed:', error)
    } finally {
      setIsLoading(false)
    }
  }, [clientWrapper, filters.archived, filters.unreadOnly, serverFilter])

  const handleLoadNewNotifications = useCallback(async () => {
    if (!clientWrapper) return


    // Get pending notifications before clearing
    const pendingNotifications = clientWrapper.getPendingNotifications()

    // Clear pending state (this will hide banner on all tabs)
    setHasPendingNotifications(false)
    setPendingNotificationCount(0)

    // Merge pending notifications with current displayed list
    if (pendingNotifications.length > 0) {

      setNotifications((prev) => {
        // Create a map to track all notification IDs (to ensure uniqueness)
        const notificationMap = new Map<string, Notification>()

        // First, add all pending notifications (they should appear at the top)
        pendingNotifications.forEach((n) => {
          notificationMap.set(n.id, n)
        })

        // Then, add all existing notifications (won't overwrite pending due to Map)
        prev.forEach((n) => {
          if (!notificationMap.has(n.id)) {
            notificationMap.set(n.id, n)
          }
        })

        // Convert back to array (pending notifications will be first)
        const mergedList = Array.from(notificationMap.values())

        // Update cache with merged list
        clientWrapper.updateCacheManually(serverFilter, mergedList, hasMore)

        return mergedList
      })

      // Scroll to top after merging notifications
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({
          top: 0,
          behavior: 'smooth'
        })
      }
    }
  }, [clientWrapper, hasMore, serverFilter])

  /**
   * Update inbox open state in wrapper and check sync status
   */
  useEffect(() => {
    if (clientWrapper) {
      clientWrapper.setInboxOpen(isOpen || false)

      // Check if needs sync when inbox is opened or tab is changed
      if (isOpen) {
        const needsInboxSync = clientWrapper.needsInboxSync()
        const needsArchiveSync = clientWrapper.needsArchiveSync()

        console.log('[NotificationInbox] Checking sync status:', {
          isOpen,
          currentTab: filters.archived ? 'archive' : 'inbox',
          needsInboxSync,
          needsArchiveSync
        })

        // Show sync banner if current tab needs sync
        if ((needsInboxSync && !filters.archived) || (needsArchiveSync && filters.archived)) {
          console.log('[NotificationInbox] Setting sync banner to true')
          setNeedsSyncBanner(true)
        } else {
          // Hide sync banner if current tab doesn't need sync
          console.log('[NotificationInbox] Setting sync banner to false')
          setNeedsSyncBanner(false)
        }
      } else {
        // Hide sync banner when inbox is closed
        setNeedsSyncBanner(false)
      }
    }
  }, [clientWrapper, isOpen, filters.archived])

  /**
   * Update current tab in wrapper
   */
  useEffect(() => {
    if (clientWrapper) {
      const currentTab = filters.archived ? 'archived' : 'inbox'
      clientWrapper.setCurrentTab(currentTab)
    }
  }, [clientWrapper, filters.archived])

  /**
   * Load notifications when inbox opens or archived filter changes
   */
  useEffect(() => {
    if (isOpen) {
      void loadNotifications()
    }
  }, [filters.archived, isOpen, loadNotifications])

  /**
   * Check for pending notifications when inbox opens
   */
  useEffect(() => {
    if (isOpen && clientWrapper) {
      const pendingCount = clientWrapper.getPendingCount()
      if (pendingCount > 0) {
        setHasPendingNotifications(true)
        setPendingNotificationCount(pendingCount)
      }
    }
  }, [isOpen, clientWrapper])

  /**
   * Listen to WebSocket events
   */
  useEffect(() => {
    if (!clientWrapper) return

    const handleNewNotificationAvailable = (data: { notification?: Notification; count: number }) => {
      console.log('[NotificationInbox] New notification available:', data)
      // Update pending state - banner visibility will be controlled by shouldShowBanner computed value
      setHasPendingNotifications(true)
      setPendingNotificationCount(data.count)
    }

    const handleNotificationReceived = () => {
      // Empty for now
    }

    const handleUnreadCountChanged = () => {
      // Empty for now
    }

    const handleSyncNeeded = (data: { tab: string; reason: string }) => {
      console.log('[NotificationInbox] Sync needed:', data)

      // Bug fix: Clear pending notifications to prevent loading stale data
      // When sync is needed due to remote operations, the pending notifications
      // may have outdated state (archived, read status, etc.)
      if (clientWrapper) {
        clientWrapper.clearPendingNotifications()
        setHasPendingNotifications(false)
        console.log('[NotificationInbox] Cleared pending notifications due to sync event')
      }

      // Only show sync banner if the event is for the current tab
      const currentTabName = filters.archived ? 'archive' : 'inbox'
      if (data.tab === currentTabName) {
        console.log('[NotificationInbox] Showing sync banner for current tab:', currentTabName)
        setNeedsSyncBanner(true)
      } else {
        console.log('[NotificationInbox] Sync needed for other tab, not showing banner')
      }
    }

    // 后台 payload 补齐完成：对当前列表套已补齐结果（作用在当前 state 对象上，
    // 保留本地乐观更新的 isRead 等状态）；一条都没变就返回原数组，不触发重渲染
    const handleNotificationsEnriched = () => {
      setNotifications((prev) => {
        let changed = false
        const next = prev.map((n) => {
          const enriched = clientWrapper.applyKnownEnrichment(n)
          if (enriched !== n) changed = true
          return enriched
        })
        return changed ? next : prev
      })
    }

    clientWrapper.on('new_notification_available', handleNewNotificationAvailable)
    clientWrapper.on('notification_received', handleNotificationReceived)
    clientWrapper.on('unread_count_changed', handleUnreadCountChanged)
    clientWrapper.on('sync_needed', handleSyncNeeded)
    clientWrapper.on('notifications_enriched', handleNotificationsEnriched)

    return () => {
      clientWrapper.off('new_notification_available', handleNewNotificationAvailable)
      clientWrapper.off('notification_received', handleNotificationReceived)
      clientWrapper.off('unread_count_changed', handleUnreadCountChanged)
      clientWrapper.off('sync_needed', handleSyncNeeded)
      clientWrapper.off('notifications_enriched', handleNotificationsEnriched)
    }
  }, [clientWrapper, filters.archived])

  /**
   * Debug cache stats
   */
  useEffect(() => {
    if (clientWrapper && process.env.NODE_ENV === 'development') {
      clientWrapper.getCacheStats()
    }
  }, [clientWrapper, notifications])

  if (!clientWrapper) {
    return (
      <div className="w-[600px] h-[600px] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  return (
    <div className={cn('w-[500px] max-w-[90vw] h-[600px] max-h-[80vh] flex flex-col bg-background', className)}>
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border">
        {/* Top row: Tabs and actions */}
        <div className="flex items-center justify-between p-4 pb-2">
          <Tabs
            value={mainTab}
            onValueChange={(value) => {
              const next = value as InboxMainTab
              const { changed, nextNovuTab, reloadNotifications } = resolveMainTabSwitch(
                mainTab,
                filters.archived ? 'archived' : 'inbox',
                next,
              )
              if (!changed) return
              setMainTab(next)
              // Novu 那侧真的翻页了才动 filters —— 切到/切出公告页时保持冻结，
              // 否则 loadNotifications 的依赖变化会在后台白拉一次列表。
              if (reloadNotifications) {
                setFilters((prev) => ({
                  ...prev,
                  archived: nextNovuTab === 'archived',
                  category: 'all',
                }))
              }
            }}
            className="w-auto"
          >
            <TabsList className="h-9">
              <TabsTrigger
                value="inbox"
                className="px-4 data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700 dark:data-[state=active]:bg-purple-900/30 dark:data-[state=active]:text-purple-300"
              >
                {t('notifications.tabs.inbox')}
              </TabsTrigger>
              <TabsTrigger
                value="archived"
                className="px-4 data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700 dark:data-[state=active]:bg-purple-900/30 dark:data-[state=active]:text-purple-300"
              >
                {t('notifications.tabs.archived')}
              </TabsTrigger>
              {/* Announcements —— R-044 从顶栏独立的 Megaphone 入口搬进来。
                  有待处理公告时挂一个不带数字的小红点（与 mobile 一致：铃铛上的
                  数字只代表 Novu 未读，公告读没读是另一套口径）。 */}
              <TabsTrigger
                value="announcements"
                className="relative px-4 data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700 dark:data-[state=active]:bg-purple-900/30 dark:data-[state=active]:text-purple-300"
              >
                {t('notifications.tabs.announcements')}
                {hasPendingAnnouncements && (
                  <span
                    data-testid="inbox-tab-announcements-dot"
                    className="ml-1.5 h-1.5 w-1.5 rounded-full bg-destructive"
                  />
                )}
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleSettingsClick}>
                  <Settings className="h-4 w-4 mr-2" />
                  {t('notifications.menu.settings')}
                </DropdownMenuItem>
                {/* Only show action buttons in Inbox tab with All category */}
                {!isAnnouncementsTab && !filters.archived && filters.category === 'all' && (
                  <>
                    <DropdownMenuItem onClick={handleMarkAllAsRead}>
                      <MailOpen className="h-4 w-4 mr-2" />
                      {t('notifications.menu.markAllAsRead')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleArchiveRead}>
                      <BookCheck className="h-4 w-4 mr-2" />
                      {t('notifications.menu.archiveRead')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleArchiveAll}>
                      <Archive className="h-4 w-4 mr-2" />
                      {t('notifications.menu.archiveAll')}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Second row: Category filters and unread toggle —— 公告页签没有这些口径 */}
        <div className={cn('flex items-center justify-between px-4 pb-3 gap-4', isAnnouncementsTab && 'hidden')}>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "h-8 px-3",
                filters.category === 'all' && "bg-purple-100 text-purple-700 hover:bg-purple-200 hover:text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 dark:hover:bg-purple-900/40 dark:hover:text-purple-200"
              )}
              onClick={() => setFilters((prev) => ({ ...prev, category: 'all' }))}
            >
              {t('notifications.categories.all')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "h-8 px-3",
                filters.category === 'mentions' && "bg-purple-100 text-purple-700 hover:bg-purple-200 hover:text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 dark:hover:bg-purple-900/40 dark:hover:text-purple-200"
              )}
              onClick={() => setFilters((prev) => ({ ...prev, category: 'mentions' }))}
            >
              {t('notifications.categories.mentions')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "h-8 px-3",
                filters.category === 'watching' && "bg-purple-100 text-purple-700 hover:bg-purple-200 hover:text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 dark:hover:bg-purple-900/40 dark:hover:text-purple-200"
              )}
              onClick={() => setFilters((prev) => ({ ...prev, category: 'watching' }))}
            >
              {t('notifications.categories.watching')}
            </Button>
          </div>

          {/* Unread Only toggle - only show in Inbox tab */}
          {!filters.archived && (
            <div className="flex items-center gap-2">
              <Label htmlFor="unread-only" className="text-sm font-normal cursor-pointer">
                {t('notifications.unreadOnly')}
              </Label>
              <Switch
                id="unread-only"
                checked={filters.unreadOnly}
                onCheckedChange={(checked) => setFilters((prev) => ({ ...prev, unreadOnly: checked }))}
              />
            </div>
          )}
        </div>
      </div>

      {/* 公告页签：列表本体与 /announcements 历史页共用 <AnnouncementList/>。
          详情不在这里渲染 —— 本组件活在铃铛的 Popover 里，弹 Dialog 会让 Popover
          因焦点移出而关闭并连带卸载详情。改成关掉收件箱 + 由全局 <AnnouncementsHost/>
          按 store 里的 id 弹详情。 */}
      {isAnnouncementsTab ? (
        <div className="flex-1 overflow-y-auto p-4">
          <AnnouncementList
            onOpenDetail={(id) => {
              dispatch(openAnnouncementDetail(id))
              onRequestClose?.()
            }}
          />
        </div>
      ) : (
      /* Content area - scrollable with fixed banner */
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto relative">
        {/* New Notifications Banner - Sticky positioned floating button */}
        {/* Banner shows only when: (1) there are pending notifications AND (2) user is on Inbox tab */}
        {/* Pending notifications banner */}
        {shouldShowPendingBanner && (
          <div className="sticky top-2 left-0 right-0 z-10 -mb-10 flex items-center justify-center pointer-events-none h-10">
            <Button
              variant="default"
              size="sm"
              className="gap-2 shadow-lg rounded-full px-4 py-1.5 h-auto text-xs font-medium pointer-events-auto"
              onClick={handleLoadNewNotifications}
            >
              <ArrowDown className="h-3.5 w-3.5" />
              {t('notifications.pending', { count: pendingNotificationCount })}
            </Button>
          </div>
        )}

        {/* Sync banner */}
        {shouldShowSyncBanner && (
          <div className="sticky top-2 left-0 right-0 z-10 -mb-10 flex items-center justify-center pointer-events-none h-10">
            <Button
              variant="default"
              size="sm"
              className="gap-2 shadow-lg rounded-full px-4 py-1.5 h-auto text-xs font-medium pointer-events-auto bg-orange-500 hover:bg-orange-600 text-white"
              onClick={handleSyncRefresh}
            >
              <RefreshCw className="h-3.5 w-3.5" />
              {t('notifications.syncBanner')}
            </Button>
          </div>
        )}

        {/* Notification content */}
        {isLoading ? (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
            {t('notifications.loading')}
          </div>
        ) : groupedNotifications.length === 0 ? (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
            {t('notifications.empty')}
          </div>
        ) : (
          <>
            {groupedNotifications.map((group) => (
              <div key={group.label}>
                {/* Group header */}
                <div className="px-4 py-2 text-xs font-semibold text-muted-foreground bg-muted/50">
                  {group.label}
                </div>

                {/* Notification list */}
                <div>
                  {group.notifications.map((notification) => {
                    // Check workflow type first
                    // Support both SDK versions: workflow?.identifier (new) and templateIdentifier (legacy)
                    const workflowIdentifier = (notification as any).workflow?.identifier || (notification as any).templateIdentifier
                    const isMsgSend = workflowIdentifier === 'msg-send'
                    const isMsgMentioned = workflowIdentifier === 'msg-mentioned'

                    // Extract user info from notification data
                    let userName = (notification.data?.userName as string) || ''
                    let userAvatar = notification.data?.userAvatar as string | undefined

                    // For msg-send or msg-mentioned workflow, extract sender data from payload or body
                    if (isMsgSend || isMsgMentioned) {
                      // 尝试多个 source,规一化为 {sender, entity, message} 形状(支持新旧 payload)
                      const rawData = notification.data as any
                      const nested = rawData?.data
                      let sender: { fullName?: string; avatarUrl?: string } | undefined
                      for (const source of [rawData, nested]) {
                        const normalized = normalizeMessagePayload(source)
                        if (normalized.sender?.fullName) {
                          sender = normalized.sender
                          break
                        }
                      }
                      if (!sender && notification.body) {
                        try {
                          const bodyData = parseNotificationBody(notification.body)
                          const normalized = normalizeMessagePayload(bodyData)
                          if (normalized.sender?.fullName) sender = normalized.sender
                        } catch (error) {
                          console.error('[msg-send/msg-mentioned notification] Error parsing body for avatar:', error)
                        }
                      }
                      if (sender) {
                        userName = sender.fullName || ''
                        userAvatar = sender.avatarUrl || undefined
                      }
                      // Last resort: extract sender name from subject
                      if (!userName && notification.subject) {
                        userName = extractSenderFromSubject(notification.subject)
                      }
                      // Avatar 兜底:notification.avatar (排除 Novu 默认的 info.svg)
                      // —— 跟 mobile NotificationItem.tsx 行为一致
                      if (!userAvatar && (notification as any).avatar &&
                          !String((notification as any).avatar).includes('novu.co/images/info')) {
                        userAvatar = (notification as any).avatar
                      }
                    }

                    // Custom action for task notifications
                    let action: string
                    if (workflowIdentifier === 'task-created') {
                      action = t('notifications.taskActions.newTaskCreated')
                    } else if (workflowIdentifier === 'task-assignment') {
                      action = t('notifications.taskActions.taskAssigned')
                    } else if (workflowIdentifier === 'task-status-updated') {
                      action = t('notifications.taskActions.taskStatusChanged')
                    } else {
                      action = (notification.data?.action as string) || notification.subject || t('notifications.sentNotification')
                    }

                    const location = notification.data?.location as string | undefined
                    const title = notification.data?.title as string | undefined

                    // Check if sender data exists and is not empty
                    const hasSenderData = userName && userName.trim() !== ''

                    // Get initials from userName for fallback avatar
                    const userInitials = userName
                      ? userName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                      : '?'

                    // Check loading and error states
                    const isLoading = loadingNotifications.has(notification.id)
                    const errorMessage = errorNotifications.get(notification.id)

                    return (
                      <div
                        key={notification.id}
                        className={cn(
                          'relative px-4 py-3 border-b border-border hover:bg-accent/50 transition-colors group',
                          !notification.isRead && 'bg-primary/5 cursor-pointer', // Only unread messages show pointer cursor
                          notification.isRead && 'cursor-default', // Read messages show default cursor
                          isLoading && 'opacity-60'
                        )}
                        onClick={() => handleNotificationClick(notification)}
                        onMouseEnter={() => setHoveredNotificationId(notification.id)}
                        onMouseLeave={() => setHoveredNotificationId(null)}
                      >
                        {/* Loading overlay */}
                        {isLoading && (
                          <div className="absolute inset-0 flex items-center justify-center bg-background/50 z-10">
                            <Loader2 className="h-5 w-5 animate-spin text-primary" />
                          </div>
                        )}

                        {/* Error banner */}
                        {errorMessage && (
                          <div className="absolute right-4 top-1/2 -translate-y-1/2 z-20 animate-in fade-in slide-in-from-right-2 duration-200">
                            <div className="bg-destructive text-destructive-foreground px-3 py-1.5 rounded-md text-xs font-medium shadow-lg flex items-center gap-2">
                              <span>{errorMessage}</span>
                            </div>
                          </div>
                        )}

                        <div className="flex gap-3">
                          {/* Avatar */}
                          <Avatar className="h-10 w-10 flex-shrink-0">
                            {!hasSenderData ? (
                              <AvatarFallback className="bg-blue-100 dark:bg-blue-950/50 text-blue-600">
                                <Info className="h-5 w-5" />
                              </AvatarFallback>
                            ) : userAvatar ? (
                              <>
                                <AvatarImage src={userAvatar} />
                                <AvatarFallback className={isMsgMentioned ? "bg-muted text-purple-600" : "bg-purple-500 text-white"}>
                                  {userInitials}
                                </AvatarFallback>
                              </>
                            ) : (
                              <AvatarFallback className={isMsgMentioned ? "bg-muted text-purple-600" : "bg-purple-500 text-white"}>
                                {userInitials}
                              </AvatarFallback>
                            )}
                          </Avatar>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            {(() => {
                              // Check if this is a msg-send workflow
                              // Support both SDK versions: workflow?.identifier (new) and templateIdentifier (legacy)
                              const workflowIdentifier = (notification as any).workflow?.identifier || (notification as any).templateIdentifier
                              const isMsgSend = workflowIdentifier === 'msg-send'

                              // Extract msg-send specific data from payload (notification.data) or body
                              const msgSendData = isMsgSend ? (() => {
                                try {
                                  // Primary: read from notification.data (raw payload)
                                  // Payload may be nested as { data: { sender: {...} } } or flat { sender: {...} }
                                  const payloadData = notification.data as any
                                  const nestedData = payloadData?.data
                                  const bodyData = payloadData?.sender ? payloadData :
                                    nestedData?.sender ? nestedData :
                                    parseNotificationBody(notification.body)

                                  const rawSender = bodyData.sender as { fullName?: string; avatarUrl?: string } | undefined
                                  // Last resort: extract sender name from subject if no sender data
                                  const senderNameFromSubject = !rawSender?.fullName ? extractSenderFromSubject(notification.subject || '') : ''
                                  // payload 镜像里 sender 恒为对象（缺省字段是 ""），只有 fullName 非空才算有发送人
                                  const sender = rawSender?.fullName
                                    ? rawSender
                                    : (senderNameFromSubject ? { fullName: senderNameFromSubject, avatarUrl: rawSender?.avatarUrl || undefined } : undefined)
                                  const entity = bodyData.entity as { id?: string; type?: string; title?: string } | undefined
                                  // Final fallback: use notification.body directly if it's plain text (not JSON)
                                  const bodyPlainText = typeof notification.body === 'string' && notification.body.trim() &&
                                    !notification.body.trim().startsWith('{') && !notification.body.trim().startsWith('[')
                                    ? notification.body.trim() : undefined
                                  const message = (bodyData.message as string | undefined) || (payloadData?.message as string | undefined) || (nestedData?.message as string | undefined) || bodyPlainText

                                  console.log('[msg-send notification] Parsed data:', { sender, entity, message, source: payloadData?.sender ? 'data' : 'body' })

                                  return {
                                    sender,
                                    entity,
                                    message,
                                    // payload 镜像里 entity 恒为对象，没有关联实体时 type/id 是 ""
                                    hasEntity: !!(entity && nonEmptyString(entity.type) && nonEmptyString(entity.id))
                                  }
                                } catch (error) {
                                  console.error('[msg-send notification] Error parsing body:', error, notification.body)
                                  return null
                                }
                              })() : null

                              if (isMsgSend && msgSendData) {
                                // Custom rendering for msg-send workflow
                                return (
                                  <>
                                    {/* First line: Title */}
                                    <div className="flex items-start justify-between gap-2 mb-1">
                                      <p className="text-sm leading-snug">
                                        {msgSendData.sender?.fullName && (
                                          <span className="text-base font-bold text-foreground">{msgSendData.sender.fullName}</span>
                                        )}
                                        {' '}
                                        {msgSendData.hasEntity ? (
                                          <>
                                            <span className="text-muted-foreground">{t('notifications.msgSend.sendMessageOn')} </span>
                                            <span className="font-semibold text-foreground">{msgSendData.entity!.type}</span>
                                          </>
                                        ) : (
                                          <span className="text-muted-foreground">{t('notifications.msgSend.sendMessage')}</span>
                                        )}
                                      </p>
                                      <div className="flex items-center gap-1 relative min-w-[16px] min-h-[16px]">
                                        {/* Hover action buttons */}
                                        {hoveredNotificationId === notification.id && !isLoading && !errorMessage && (
                                          <div className="flex gap-1 animate-in fade-in duration-200 absolute right-0 top-0">
                                            {/* Archive/Unarchive button */}
                                            {notification.isArchived ? (
                                              // Hide unarchive button in Archive tab, but keep the logic
                                              !filters.archived && (
                                                <Button
                                                  variant="ghost"
                                                  size="icon"
                                                  className="h-6 w-6"
                                                  onClick={(e) => handleUnarchive(notification, e)}
                                                  title={t('notifications.actions.unarchive')}
                                                  disabled={isLoading}
                                                >
                                                  <ArchiveRestore className="h-3.5 w-3.5" />
                                                </Button>
                                              )
                                            ) : (
                                              <>
                                                <Button
                                                  variant="ghost"
                                                  size="icon"
                                                  className="h-6 w-6"
                                                  onClick={(e) => handleArchive(notification, e)}
                                                  title={t('notifications.actions.archive')}
                                                  disabled={isLoading}
                                                >
                                                  <Archive className="h-3.5 w-3.5" />
                                                </Button>
                                                <Button
                                                  variant="ghost"
                                                  size="icon"
                                                  className="h-6 w-6"
                                                  onClick={(e) => handleToggleRead(notification, e)}
                                                  title={notification.isRead ? t('notifications.actions.markAsUnread') : t('notifications.actions.markAsRead')}
                                                  disabled={isLoading}
                                                >
                                                  {notification.isRead ? (
                                                    <Mail className="h-3.5 w-3.5" />
                                                  ) : (
                                                    <MailOpen className="h-3.5 w-3.5" />
                                                  )}
                                                </Button>
                                              </>
                                            )}
                                          </div>
                                        )}
                                        {/* Unread badge */}
                                        {!notification.isRead && hoveredNotificationId !== notification.id && !isLoading && (
                                          <Badge className="h-2 w-2 p-0 rounded-full bg-primary" />
                                        )}
                                      </div>
                                    </div>

                                    {/* Second line: Time */}
                                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                                      <span>{formatTimestamp(notification.createdAt)}</span>
                                    </div>

                                    {/* Third section: Message details */}
                                    <div className="text-sm text-foreground bg-blue-50 dark:bg-blue-950/30 px-3 py-2 rounded space-y-1.5">
                                      {msgSendData.hasEntity && (
                                        <div>
                                          <span className="font-semibold">{msgSendData.entity!.type} title: </span>
                                          <a
                                            href={`/${msgSendData.entity!.type}s/${msgSendData.entity!.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            {msgSendData.entity!.title || t('notifications.untitled')}
                                          </a>
                                        </div>
                                      )}
                                      {msgSendData.message && (
                                        <div className="break-words whitespace-pre-wrap">
                                          {msgSendData.message}
                                        </div>
                                      )}
                                    </div>
                                  </>
                                )
                              }

                              // Check if this is a msg-mentioned workflow
                              const isMsgMentioned = workflowIdentifier === 'msg-mentioned'

                              // Extract msg-mentioned specific data from payload (notification.data) or body
                              const msgMentionedData = isMsgMentioned ? (() => {
                                try {
                                  // 多源 merge:notification.data / nested data / parsed body 三个来源,
                                  // 每个字段(sender / entity / message)各取第一个非空值。这样即使某个
                                  // source 只有 sender 没 entity、另一个反过来,也能拼出完整数据。
                                  const payloadData = notification.data as any
                                  const nestedData = payloadData?.data
                                  const bodyData = parseNotificationBody(notification.body)

                                  let sender: { fullName?: string; avatarUrl?: string } | undefined
                                  let entity: { id?: string; type?: string; title?: string } | undefined
                                  let message: string | undefined
                                  for (const source of [payloadData, nestedData, bodyData]) {
                                    const n = normalizeMessagePayload(source)
                                    if (!sender?.fullName && n.sender) sender = n.sender
                                    if (!entity && n.entity) entity = n.entity
                                    if (!message && n.message) message = n.message
                                  }

                                  // sender 名字最终兜底:从 subject "X mentioned you" 解
                                  if (!sender?.fullName && notification.subject) {
                                    const nameFromSubject = extractSenderFromSubject(notification.subject)
                                    if (nameFromSubject) sender = { ...(sender || {}), fullName: nameFromSubject }
                                  }

                                  // avatar 兜底:notification.avatar (排除 Novu 默认 info.svg)
                                  const notifAvatar = (notification as any).avatar
                                  if (!sender?.avatarUrl && notifAvatar &&
                                      !String(notifAvatar).includes('novu.co/images/info')) {
                                    sender = { ...(sender || {}), avatarUrl: notifAvatar }
                                  }

                                  // message 最终兜底:body 是纯文本(非 JSON)时直接用
                                  if (!message && typeof notification.body === 'string') {
                                    const trimmed = notification.body.trim()
                                    if (trimmed && !trimmed.startsWith('{') && !trimmed.startsWith('[')) {
                                      message = trimmed
                                    }
                                  }

                                  console.log('[msg-mentioned notification] Parsed data:', { sender, entity, message })

                                  return {
                                    sender,
                                    entity,
                                    message,
                                    hasEntity: !!(entity && nonEmptyString(entity.type) && nonEmptyString(entity.id))
                                  }
                                } catch (error) {
                                  console.error('[msg-mentioned notification] Error parsing body:', error, notification.body)
                                  return null
                                }
                              })() : null

                              if (isMsgMentioned && msgMentionedData) {
                                // Custom rendering for msg-mentioned workflow
                                return (
                                  <>
                                    {/* First line: Title */}
                                    <div className="flex items-start justify-between gap-2 mb-1">
                                      <p className="text-sm leading-snug">
                                        {msgMentionedData.sender?.fullName && (
                                          <span className="text-base font-bold text-foreground">{msgMentionedData.sender.fullName}</span>
                                        )}
                                        {' '}
                                        {msgMentionedData.hasEntity ? (
                                          <>
                                            <span className="text-muted-foreground">{t('notifications.msgMentioned.mentionedOn')} </span>
                                            <span className="font-semibold text-foreground">{entityTypeDisplay(msgMentionedData.entity!.type)}</span>
                                          </>
                                        ) : (
                                          <span className="text-muted-foreground">{t('notifications.msgMentioned.mentioned')}</span>
                                        )}
                                      </p>
                                      <div className="flex items-center gap-1 relative min-w-[16px] min-h-[16px]">
                                        {/* Hover action buttons */}
                                        {hoveredNotificationId === notification.id && !isLoading && !errorMessage && (
                                          <div className="flex gap-1 animate-in fade-in duration-200 absolute right-0 top-0">
                                            {notification.isArchived ? (
                                              // Hide unarchive button in Archive tab, but keep the logic
                                              !filters.archived && (
                                                <Button
                                                  variant="ghost"
                                                  size="icon"
                                                  className="h-6 w-6"
                                                  onClick={(e) => handleUnarchive(notification, e)}
                                                  title={t('notifications.actions.unarchive')}
                                                  disabled={isLoading}
                                                >
                                                  <ArchiveRestore className="h-3.5 w-3.5" />
                                                </Button>
                                              )
                                            ) : (
                                              <>
                                                <Button
                                                  variant="ghost"
                                                  size="icon"
                                                  className="h-6 w-6"
                                                  onClick={(e) => handleArchive(notification, e)}
                                                  title={t('notifications.actions.archive')}
                                                  disabled={isLoading}
                                                >
                                                  <Archive className="h-3.5 w-3.5" />
                                                </Button>
                                                <Button
                                                  variant="ghost"
                                                  size="icon"
                                                  className="h-6 w-6"
                                                  onClick={(e) => handleToggleRead(notification, e)}
                                                  title={notification.isRead ? t('notifications.actions.markAsUnread') : t('notifications.actions.markAsRead')}
                                                  disabled={isLoading}
                                                >
                                                  {notification.isRead ? (
                                                    <Mail className="h-3.5 w-3.5" />
                                                  ) : (
                                                    <MailOpen className="h-3.5 w-3.5" />
                                                  )}
                                                </Button>
                                              </>
                                            )}
                                          </div>
                                        )}
                                        {!notification.isRead && hoveredNotificationId !== notification.id && !isLoading && (
                                          <Badge className="h-2 w-2 p-0 rounded-full bg-primary" />
                                        )}
                                      </div>
                                    </div>

                                    {/* Second line: Time */}
                                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                                      <span>{formatTimestamp(notification.createdAt)}</span>
                                    </div>

                                    {/* Third section: 单行内联渲染 → @xxx message on <link>task title</link>
                                        跟 mobile NotificationItem 行为一致: @xxx 紫色加粗,task title 蓝色下划线链接 */}
                                    <div className="text-sm text-foreground bg-blue-50 dark:bg-blue-950/30 px-3 py-2 rounded break-words leading-relaxed">
                                      {msgMentionedData.message && renderMentionContent(msgMentionedData.message)}
                                      {msgMentionedData.hasEntity && (() => {
                                        const displayType = entityTypeDisplay(msgMentionedData.entity!.type)
                                        const linkLabel = msgMentionedData.entity!.title ||
                                          (displayType ? displayType.charAt(0).toUpperCase() + displayType.slice(1) : '')
                                        const href = entityHref(msgMentionedData.entity!.type, msgMentionedData.entity!.id)
                                        return (
                                          <>
                                            <span> on </span>
                                            {href ? (
                                              <a
                                                href={href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:text-blue-800 underline font-medium dark:text-blue-400 dark:hover:text-blue-300"
                                                onClick={(e) => e.stopPropagation()}
                                              >
                                                {linkLabel}
                                              </a>
                                            ) : (
                                              <span className="text-blue-600 font-medium">{linkLabel}</span>
                                            )}
                                          </>
                                        )
                                      })()}
                                    </div>
                                  </>
                                )
                              }

                              // Default rendering for other workflows
                              return (
                                <>
                                  {/* First line: Name and action */}
                                  <div className="flex items-start justify-between gap-2 mb-1">
                                    <p className="text-sm">
                                      {hasSenderData && (
                                        <>
                                          <span className="font-semibold text-foreground">{userName}</span>{' '}
                                        </>
                                      )}
                                      <span className="text-muted-foreground">{action}</span>
                                    </p>
                                    <div className="flex items-center gap-1 relative min-w-[16px] min-h-[16px]">
                                      {/* Hover action buttons */}
                                      {hoveredNotificationId === notification.id && !isLoading && !errorMessage && (
                                        <div className="flex gap-1 animate-in fade-in duration-200 absolute right-0 top-0">
                                          {/* Archive/Unarchive button */}
                                          {notification.isArchived ? (
                                            // Hide unarchive button in Archive tab, but keep the logic
                                            !filters.archived && (
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6"
                                                onClick={(e) => handleUnarchive(notification, e)}
                                                title={t('notifications.actions.unarchive')}
                                                disabled={isLoading}
                                              >
                                                <ArchiveRestore className="h-3.5 w-3.5" />
                                              </Button>
                                            )
                                          ) : (
                                            // Inbox tab: show both archive and read/unread buttons
                                            <>
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6"
                                                onClick={(e) => handleArchive(notification, e)}
                                                title={t('notifications.actions.archive')}
                                                disabled={isLoading}
                                              >
                                                <Archive className="h-3.5 w-3.5" />
                                              </Button>
                                              {/* Read/Unread button - only in Inbox */}
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6"
                                                onClick={(e) => handleToggleRead(notification, e)}
                                                title={notification.isRead ? t('notifications.actions.markAsUnread') : t('notifications.actions.markAsRead')}
                                                disabled={isLoading}
                                              >
                                                {notification.isRead ? (
                                                  <Mail className="h-3.5 w-3.5" />
                                                ) : (
                                                  <MailOpen className="h-3.5 w-3.5" />
                                                )}
                                              </Button>
                                            </>
                                          )}
                                        </div>
                                      )}
                                      {/* Unread badge */}
                                      {!notification.isRead && hoveredNotificationId !== notification.id && !isLoading && (
                                        <Badge className="h-2 w-2 p-0 rounded-full bg-primary" />
                                      )}
                                    </div>
                                  </div>

                                  {/* Second line: Time and location */}
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                                    <span>{formatTimestamp(notification.createdAt)}</span>
                                    {location && (
                                      <>
                                        <span>•</span>
                                        <span>{location}</span>
                                      </>
                                    )}
                                  </div>

                                  {/* Title */}
                                  {title && (
                                    <div className="text-sm font-medium text-foreground mb-1">{title}</div>
                                  )}
                                </>
                              )
                            })()}

                            {/* Content preview */}
                            {notification.body && (() => {
                              // Check if this is a task-created workflow
                              // Support both SDK versions: workflow?.identifier (new) and templateIdentifier (legacy)
                              const workflowIdentifier = (notification as any).workflow?.identifier || (notification as any).templateIdentifier

                              // Skip rendering body for msg-send and msg-mentioned workflows (already rendered above)
                              if (workflowIdentifier === 'msg-send' || workflowIdentifier === 'msg-mentioned') {
                                return null
                              }

                              if (workflowIdentifier === 'task-created') {
                                try {
                                  // Parse the JSON string from body
                                  const bodyData = typeof notification.body === 'string'
                                    ? safeJsonParse(notification.body.replace(/'/g, '"'))
                                    : notification.body

                                  const task = bodyData?.task

                                  if (task) {
                                    const taskTitle = getReadableTaskTitle(task)
                                    // Render task-created notification format
                                    return (
                                      <div className="text-sm text-foreground px-3 py-2 rounded space-y-1.5">
                                        <div>
                                          <span className="font-semibold">{t('notifications.taskNotification.taskTitle')}</span>
                                          <a
                                            href={`/tasks/${task.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            {taskTitle}
                                          </a>
                                        </div>
                                        {task.creator?.fullName && (
                                          <div>
                                            <span className="font-semibold">{t('notifications.taskNotification.createdBy')}</span>
                                            <span>{task.creator.fullName}</span>
                                          </div>
                                        )}
                                      </div>
                                    )
                                  } else {
                                    // No task data found - still render custom format to show it's being processed
                                    return (
                                      <div className="text-sm text-foreground bg-yellow-50 dark:bg-yellow-950/30 px-3 py-2 rounded">
                                        ⚠️ Task notification but no task data found
                                      </div>
                                    )
                                  }
                                } catch (error) {
                                  console.error('❌ Error parsing task-created notification body:', error, notification.body)
                                  // Parse error - show error message
                                  return (
                                    <div className="text-sm text-foreground bg-red-50 dark:bg-red-950/30 px-3 py-2 rounded">
                                      ❌ Error parsing task notification
                                    </div>
                                  )
                                }
                              }

                              // task-assignment notification renderer
                              if (workflowIdentifier === 'task-assignment') {
                                try {
                                  // Parse the JSON string from body
                                  const bodyData = typeof notification.body === 'string'
                                    ? safeJsonParse(notification.body.replace(/'/g, '"'))
                                    : notification.body

                                  const task = bodyData?.task

                                  if (task) {
                                    const taskTitle = getReadableTaskTitle(task)
                                    const assigneeName = task.assignee?.fullName || task.assigneeName || bodyData?.assigneeName || null
                                    // Render task-assignment notification format
                                    return (
                                      <div className="text-sm text-foreground px-3 py-2 rounded space-y-1.5">
                                        <div>
                                          <span className="font-semibold">{t('notifications.taskNotification.taskTitle')}</span>
                                          <a
                                            href={`/tasks/${task.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            {taskTitle}
                                          </a>
                                        </div>
                                        {assigneeName && (
                                          <div>
                                            <span className="font-semibold">{t('notifications.taskNotification.assignedTo')}</span>
                                            <span>{assigneeName}</span>
                                          </div>
                                        )}
                                        {task.creator?.fullName && (
                                          <div>
                                            <span className="font-semibold">{t('notifications.taskNotification.createdBy')}</span>
                                            <span>{task.creator.fullName}</span>
                                          </div>
                                        )}
                                      </div>
                                    )
                                  } else {
                                    // No task data found - still render custom format to show it's being processed
                                    return (
                                      <div className="text-sm text-foreground bg-yellow-50 dark:bg-yellow-950/30 px-3 py-2 rounded">
                                        ⚠️ Task assignment notification but no task data found
                                      </div>
                                    )
                                  }
                                } catch (error) {
                                  console.error('❌ Error parsing task-assignment notification body:', error, notification.body)
                                  // Parse error - show error message
                                  return (
                                    <div className="text-sm text-foreground bg-red-50 dark:bg-red-950/30 px-3 py-2 rounded">
                                      ❌ Error parsing task assignment notification
                                    </div>
                                  )
                                }
                              }

                              // task-status-updated notification renderer
                              if (workflowIdentifier === 'task-status-updated') {
                                try {
                                  // Parse the JSON string from body
                                  const bodyData = typeof notification.body === 'string'
                                    ? safeJsonParse(notification.body.replace(/'/g, '"'))
                                    : notification.body

                                  const task = bodyData?.task

                                  if (task) {
                                    const taskTitle = getReadableTaskTitle(task)
                                    // Render task-status-updated notification format
                                    return (
                                      <div className="text-sm text-foreground px-3 py-2 rounded space-y-1.5">
                                        <div>
                                          <span className="font-semibold">{t('notifications.taskNotification.taskTitle')}</span>
                                          <a
                                            href={`/tasks/${task.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            {taskTitle}
                                          </a>
                                        </div>
                                        {task.creator?.fullName && (
                                          <div>
                                            <span className="font-semibold">{t('notifications.taskNotification.createdBy')}</span>
                                            <span>{task.creator.fullName}</span>
                                          </div>
                                        )}
                                      </div>
                                    )
                                  } else {
                                    // No task data found - still render custom format to show it's being processed
                                    return (
                                      <div className="text-sm text-foreground bg-yellow-50 dark:bg-yellow-950/30 px-3 py-2 rounded">
                                        ⚠️ Task status updated notification but no task data found
                                      </div>
                                    )
                                  }
                                } catch (error) {
                                  console.error('❌ Error parsing task-status-updated notification body:', error, notification.body)
                                  // Parse error - show error message
                                  return (
                                    <div className="text-sm text-foreground bg-red-50 dark:bg-red-950/30 px-3 py-2 rounded">
                                      ❌ Error parsing task status update notification
                                    </div>
                                  )
                                }
                              }

                              // Default notification renderer
                              return (
                                <div className="text-sm text-foreground bg-blue-50 dark:bg-blue-950/30 px-3 py-2 rounded">
                                  {notification.body}
                                </div>
                              )
                            })()}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}

            {/* Load more */}
            {hasMore && (
              <div className="sticky bottom-0 left-0 right-0 p-3 text-center border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
                <Button
                  variant="outline"
                  onClick={handleLoadMore}
                  disabled={isFetching}
                  className="w-full shadow-sm hover:shadow-md transition-shadow"
                >
                  {isFetching ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('notifications.loadingMore')}
                    </>
                  ) : (
                    t('notifications.loadMore')
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      )}
    </div>
  )
}

// clearNotificationInboxCache 已移至 ./notification-inbox-cache —— 它的调用方是
// api 层（登出 / token 过期），不该为此 import 本组件。这里刻意**不**做 re-export：
// 留一条转发路径会让 api 层继续把整个组件拉进模块图，等于没修。
